import axios from "axios";
import type { TaskDetailItem, TaskRowColor } from "./requests";
import { readPaginationHeaders, toApiPage, toPageIndex } from "../utils/apiPagination";

export type PolicyCheckTaskStatus = "new" | "done" | "error" | "in_progress";

export type PolicyCheckReportResult = "found" | "not_found" | "error";

export type PolicyCheckReportColor = "red" | "green" | "yellow" | "default";

export interface PolicyCheckCreated {
  id?: string;
}

export interface PolicyCheckCompany {
  id?: string;
  companyName?: string;
  inn?: string;
}

export interface PolicyCheckTaskListItem {
  id?: string;
  status?: PolicyCheckTaskStatus | string;
  startDateTime?: string;
  endDateTime?: string | null;
  policyFileName?: string;
}

export interface PolicyCheckReportItem {
  taskId?: string;
  checkName?: string;
  checkNameDescription?: string;
  result?: PolicyCheckReportResult | string;
  resultDescription?: string;
  details?: string;
  suggestion?: string;
  responsibility?: string;
  color?: PolicyCheckReportColor | string;
}

export interface PolicyCheckReport {
  requestId?: string;
  startDateTime?: string;
  endDateTime?: string | null;
  status?: PolicyCheckTaskStatus | string;
  tasks?: PolicyCheckReportItem[];
}

export interface PolicyCheckCompanyListResponse {
  items: PolicyCheckCompany[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
}

export interface PolicyCheckTaskListResponse {
  items: PolicyCheckTaskListItem[];
  page: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
}

export interface CreatePolicyCheckPayload {
  inn: string;
  companyName: string;
  file: File;
}

export const createPolicyCheck = (payload: CreatePolicyCheckPayload): Promise<PolicyCheckCreated> => {
  const formData = new FormData();
  formData.append("inn", payload.inn);
  formData.append("companyName", payload.companyName);
  formData.append("file", payload.file);

  return axios
    .post("/check/policy", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((resp) => resp.data);
};

export const createPolicyCheckVersion = (
  policyCheckRequestId: string,
  file: File
): Promise<PolicyCheckCreated> => {
  const formData = new FormData();
  formData.append("file", file);

  return axios
    .post(`/check/policy/${encodeURIComponent(policyCheckRequestId.trim())}/version`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((resp) => resp.data);
};

export const fetchPolicyCheckCompanies = (params: {
  page?: number;
  pageSize?: number;
} = {}): Promise<PolicyCheckCompanyListResponse> => {
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const apiPage = toApiPage(pageIndex);

  return axios
    .get<PolicyCheckCompany[]>("/check/policy/all", {
      params: { page: apiPage, pageSize },
    })
    .then((resp) => {
      const items = Array.isArray(resp.data) ? resp.data : [];
      const pagination = readPaginationHeaders(resp.headers ?? {}, {
        page: apiPage,
        pageSize,
        itemsLength: items.length,
      });

      return {
        items,
        page: toPageIndex(pagination.page),
        pageSize: pagination.pageSize,
        totalPages: pagination.totalPages,
        totalElements: pagination.totalElements,
      };
    });
};

export const fetchPolicyCheckTasks = (
  id: string,
  params: {
    page?: number;
    pageSize?: number;
    sorting?: "asc" | "desc";
  } = {}
): Promise<PolicyCheckTaskListResponse> => {
  const pageIndex = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const apiPage = toApiPage(pageIndex);

  return axios
    .get<PolicyCheckTaskListItem[]>(`/check/policy/${id}`, {
      params: {
        page: apiPage,
        pageSize,
        sorting: params.sorting ?? "desc",
      },
    })
    .then((resp) => {
      const items = Array.isArray(resp.data) ? resp.data : [];
      const pagination = readPaginationHeaders(resp.headers ?? {}, {
        page: apiPage,
        pageSize,
        itemsLength: items.length,
      });

      return {
        items,
        page: toPageIndex(pagination.page),
        pageSize: pagination.pageSize,
        totalPages: pagination.totalPages,
        totalElements: pagination.totalElements,
      };
    });
};

export const fetchPolicyCheckResult = (fileId: string): Promise<PolicyCheckReport> =>
  axios
    .get(`/check/policy/result/${encodeURIComponent(fileId.trim())}`)
    .then((resp) => resp.data);

export const fetchPolicyCheckCompanyById = async (
  id: string
): Promise<PolicyCheckCompany | null> => {
  const pageSize = 100;
  let page = 0;
  let totalPages = 1;

  while (page < totalPages) {
    const response = await fetchPolicyCheckCompanies({ page, pageSize });
    const match = response.items.find((item) => item.id === id);
    if (match) return match;
    totalPages = Math.max(1, response.totalPages);
    page += 1;
  }

  return null;
};

const toTaskRowColor = (color?: string): TaskRowColor => {
  if (color === "red" || color === "green" || color === "yellow") return color;
  return null;
};

export const mapPolicyReportToTaskItems = (report: PolicyCheckReport): TaskDetailItem[] =>
  (report.tasks ?? []).map((task) => ({
    taskId: task.taskId ?? "",
    checkName: task.checkName ?? "",
    checkNameDescription: task.checkNameDescription ?? "",
    result: task.result ?? "",
    resultDescription: task.resultDescription ?? "",
    details: task.details,
    suggestion: task.suggestion ?? "",
    responsibility: task.responsibility ?? "",
    color: toTaskRowColor(task.color),
  }));

export const isPolicyCheckPendingStatus = (status?: string | null): boolean =>
  status === "new" || status === "in_progress";
